import { useCallback, useEffect, useMemo, useState } from 'react'
import type {
  ActiveTaskEntry,
  ProjectProfitabilityEntry,
} from '../components/ProfitabilityPerformance/mockData'
import { ACTIVE_TASKS_PAGE_SIZE } from '../services/pwfActiveTasksService'
import {
  fetchProfitabilitySnapshotFromFirestore,
  filterActiveTasksByDueDateRange,
  filterCompletedTasksByDateRange,
  customTrackedTimeUnavailableMessage,
  resolveActiveTaskDueDateRange,
  resolveEmployeeTimeTrackedFromSnapshot,
  savePwfProfitabilitySnapshotFromPwf,
  type ProfitabilitySnapshotData,
} from '../services/pwfProfitabilitySnapshotService'
import type { PwfActiveTasksSummary, PwfEmployeeTimeSpent } from '../types'

export function useProfitabilityPwfData(
  dateRange: { from: string; to: string },
  datePreset: 'mtd' | 'custom' = 'custom'
) {
  const [snapshot, setSnapshot] = useState<ProfitabilitySnapshotData | null>(null)
  const [activeTasksVisibleCount, setActiveTasksVisibleCount] = useState(ACTIVE_TASKS_PAGE_SIZE)
  const [loading, setLoading] = useState(true)
  const [fetchingFromPwf, setFetchingFromPwf] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fetchNotice, setFetchNotice] = useState<string | null>(null)
  const [customTrackedTime, setCustomTrackedTime] = useState<PwfEmployeeTimeSpent[]>([])
  const [customTrackedTimeLoading, setCustomTrackedTimeLoading] = useState(false)
  const [customTrackedTimeError, setCustomTrackedTimeError] = useState<string | null>(null)

  const applySnapshot = useCallback((data: ProfitabilitySnapshotData) => {
    setSnapshot(data)
    setActiveTasksVisibleCount(ACTIVE_TASKS_PAGE_SIZE)
  }, [])

  const loadSnapshot = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchProfitabilitySnapshotFromFirestore()
      applySnapshot(data)
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to load ProWorkflow snapshot'
      setError(message)
      setSnapshot(null)
    } finally {
      setLoading(false)
    }
  }, [applySnapshot])

  const fetchFromPwf = useCallback(async () => {
    setFetchingFromPwf(true)
    setError(null)
    setFetchNotice(null)
    try {
      const result = await savePwfProfitabilitySnapshotFromPwf()
      const data = await fetchProfitabilitySnapshotFromFirestore()
      applySnapshot(data)

      const parts = [
        `${result.completedTasksCount} completed`,
        `${result.activeTasksCount} active`,
        `${result.projectProfitabilityCount} projects`,
      ]
      if (result.invoicedProjectCount != null) {
        parts.push(`${result.invoicedProjectCount} invoiced`)
      }
      let notice = `Snapshot saved to Firestore (${parts.join(', ')}).`
      if (
        result.completedTasksCount === 0 &&
        result.activeTasksCount === 0 &&
        result.projectProfitabilityCount === 0
      ) {
        notice += ' PWF returned no tasks in the selected window.'
      }
      setFetchNotice(notice)
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to fetch ProWorkflow snapshot'
      setError(message)
    } finally {
      setFetchingFromPwf(false)
    }
  }, [applySnapshot])

  useEffect(() => {
    if (datePreset !== 'custom') {
      setCustomTrackedTime([])
      setCustomTrackedTimeError(null)
      setCustomTrackedTimeLoading(false)
      return
    }
    if (!dateRange.from || !dateRange.to) return
    if (loading) return

    const fromSnapshot = snapshot
      ? resolveEmployeeTimeTrackedFromSnapshot(dateRange.from, dateRange.to, {
          employeeTimeSpentMtd: snapshot.employeeTimeSpentMtd ?? [],
          employeeTimeSpentByMonth: snapshot.employeeTimeSpentByMonth ?? {},
          employeeTimeSpentByDay: snapshot.employeeTimeSpentByDay ?? {},
          mtdTimeTrackedFrom: snapshot.mtdTimeTrackedFrom,
          mtdTimeTrackedTo: snapshot.mtdTimeTrackedTo,
        })
      : null

    if (fromSnapshot !== null) {
      setCustomTrackedTime(fromSnapshot)
      setCustomTrackedTimeError(null)
      setCustomTrackedTimeLoading(false)
      return
    }

    setCustomTrackedTime([])
    setCustomTrackedTimeError(
      customTrackedTimeUnavailableMessage(dateRange.from, dateRange.to, snapshot)
    )
    setCustomTrackedTimeLoading(false)
  }, [datePreset, dateRange.from, dateRange.to, snapshot, loading])

  const activeTaskDueRange = useMemo(
    () => resolveActiveTaskDueDateRange(datePreset, dateRange),
    [datePreset, dateRange.from, dateRange.to]
  )

  const loadMoreActiveTasks = useCallback(() => {
    setActiveTasksVisibleCount((prev) => {
      const total = filterActiveTasksByDueDateRange(
        snapshot?.activeTasks ?? [],
        activeTaskDueRange
      ).length
      return Math.min(prev + ACTIVE_TASKS_PAGE_SIZE, total)
    })
  }, [snapshot, activeTaskDueRange])

  useEffect(() => {
    void loadSnapshot()
  }, [loadSnapshot])

  useEffect(() => {
    setActiveTasksVisibleCount(ACTIVE_TASKS_PAGE_SIZE)
  }, [activeTaskDueRange.from, activeTaskDueRange.to])

  const allCompletedTasks = snapshot?.completedTasks ?? []
  const completedTasks = useMemo(
    () => filterCompletedTasksByDateRange(allCompletedTasks, dateRange),
    [allCompletedTasks, dateRange.from, dateRange.to]
  )

  const projectProfitability: ProjectProfitabilityEntry[] = snapshot?.projectProfitability ?? []
  const allActiveTasks: ActiveTaskEntry[] = snapshot?.activeTasks ?? []
  const activeTasksInDateRange = useMemo(
    () => filterActiveTasksByDueDateRange(allActiveTasks, activeTaskDueRange),
    [allActiveTasks, activeTaskDueRange.from, activeTaskDueRange.to]
  )
  const activeTasks = useMemo(
    () => activeTasksInDateRange.slice(0, activeTasksVisibleCount),
    [activeTasksInDateRange, activeTasksVisibleCount]
  )
  const activeTasksSummary: PwfActiveTasksSummary | null = snapshot?.activeTasksSummary ?? null
  const employeeTaskTime = snapshot?.employeeTaskTime ?? []
  const employeeTimeSpent: PwfEmployeeTimeSpent[] = snapshot?.employeeTimeSpent ?? []
  const employeeTimeSpentMtd: PwfEmployeeTimeSpent[] = snapshot?.employeeTimeSpentMtd ?? []
  const taskCompletionDates = snapshot?.taskCompletionDates ?? {}
  const activeTasksHasMore = activeTasksVisibleCount < activeTasksInDateRange.length
  const activeTasksTotalMatched = activeTasksInDateRange.length
  const mtdInvoiceTotal = snapshot?.mtdInvoiceTotal ?? 0
  const recordedAt = snapshot?.recordedAt ?? null

  return {
    completedTasks,
    allCompletedTasks,
    projectProfitability,
    activeTasks,
    activeTasksInDateRange,
    activeTaskDueRange,
    allActiveTasks,
    activeTasksSummary,
    employeeTaskTime,
    employeeTimeSpent,
    employeeTimeSpentMtd,
    customTrackedTime,
    customTrackedTimeLoading,
    customTrackedTimeError,
    taskCompletionDates,
    activeTasksHasMore,
    activeTasksTotalMatched,
    mtdInvoiceTotal,
    loading,
    fetchingFromPwf,
    loadingMoreActiveTasks: false,
    error,
    fetchNotice,
    recordedAt,
    refetch: loadSnapshot,
    fetchFromPwf,
    loadMoreActiveTasks,
  }
}
